import type { TransportDirection, TransportListItem, TransportStatus } from '@proerp/shared';

/**
 * Beschriftungen des Minimalmoduls Transport (ADR 0079).
 *
 * Der Kern: **ein Status-Enum, zwei Beschriftungen.** Ein eingehender Transport
 * „kommt an", ein ausgehender „fährt ab" — im Backend ist beides `ABGEFAHREN`. Die
 * Übersetzung passiert hier und NUR hier, damit Liste, Detail und Dialog dasselbe
 * Wort benutzen.
 */

export const TRANSPORT_STATUS_META: Record<
  TransportStatus,
  { label: string; tone: 'neutral' | 'warning' | 'info' | 'success' }
> = {
  ENTWURF: { label: 'Entwurf', tone: 'neutral' },
  GEPLANT: { label: 'Geplant', tone: 'info' },
  VERLADEN: { label: 'Verladen', tone: 'warning' },
  ABGEFAHREN: { label: 'Abgefahren', tone: 'success' },
  STORNIERT: { label: 'Storniert', tone: 'neutral' },
};

/** Richtungsabhängiges Statuswort. */
export function transportStatusLabel(status: TransportStatus, direction: TransportDirection): string {
  if (direction === 'INBOUND' && status === 'ABGEFAHREN') return 'Angekommen';
  return TRANSPORT_STATUS_META[status].label;
}

export const TRANSPORT_DIRECTION_META: Record<TransportDirection, { label: string; short: string }> = {
  OUTBOUND: { label: 'Ausgehend (Abholung)', short: 'Ausgehend' },
  INBOUND: { label: 'Eingehend (Anlieferung)', short: 'Eingehend' },
};

export const TRANSPORT_DIRECTION_ORDER: TransportDirection[] = ['INBOUND', 'OUTBOUND'];

/**
 * Der nächste sinnvolle Schritt — genau EIN Knopf statt einer Statusliste. `null`,
 * wenn nichts mehr zu tun ist (abgefahren/storniert).
 */
export function nextTransportStep(
  status: TransportStatus,
  direction: TransportDirection,
): { status: TransportStatus; label: string } | null {
  if (status === 'ENTWURF') return { status: 'GEPLANT', label: 'Einplanen' };
  if (status === 'GEPLANT' || status === 'VERLADEN') {
    return {
      status: 'ABGEFAHREN',
      label: direction === 'INBOUND' ? 'Als angekommen buchen' : 'Als abgefahren buchen',
    };
  }
  return null;
}

// ── Sortierung (Liste) ───────────────────────────────────────────────────────

export type TransportSortKey = 'plannedAt' | 'number';

/**
 * Client-seitig, weil die Liste ohnehin komplett geladen wird (§ `transportApi`).
 * Termin-lose Transporte landen immer am Ende, unabhängig von der Richtung —
 * ein fehlender Termin soll nicht durch Umdrehen nach oben springen.
 */
export function compareTransports(
  a: TransportListItem,
  b: TransportListItem,
  sort: { key: TransportSortKey; dir: 'asc' | 'desc' },
): number {
  const factor = sort.dir === 'asc' ? 1 : -1;
  if (sort.key === 'number') return a.number.localeCompare(b.number) * factor;
  if (!a.plannedAt && !b.plannedAt) return 0;
  if (!a.plannedAt) return 1;
  if (!b.plannedAt) return -1;
  return a.plannedAt.localeCompare(b.plannedAt) * factor;
}

// ── Feldbeschriftungen (richtungsabhängig) ───────────────────────────────────

export function locationFieldLabel(direction: TransportDirection): string {
  return direction === 'INBOUND' ? 'Herkunft / Lieferant' : 'Ziel / Abladestelle';
}

/** Der Termin — das Feld, um das es im Minimalmodul geht. */
export function plannedAtFieldLabel(direction: TransportDirection): string {
  return direction === 'INBOUND' ? 'Ankunft (Datum + Uhrzeit)' : 'Abholung (Datum + Uhrzeit)';
}

export function completedAtFieldLabel(direction: TransportDirection): string {
  return direction === 'INBOUND' ? 'Angekommen' : 'Abgefahren';
}

/** Beschriftung der Verknüpfung — der eine Unterschied zwischen den Richtungen. */
export function linkFieldLabel(direction: TransportDirection): string {
  return direction === 'INBOUND' ? 'Bestellungen' : 'Projekt';
}
